// The org-key gate (epic #3629, child #3632): does this account hold ONLY
// personal keys?
//
// `validOrgKey` (`packages/db/src/queries/byok/predicates.ts`) requires
// `user_id IS NULL` — a personal key never satisfies the funnel's `key_set`
// hop, scout capacity, or any org run. Prod on 2026-09-10 held an `active`
// account with four personal keys and ZERO usable ones, and neither keys page
// said so: the org list read "no keys yet" and the personal list read "valid".
// This is the one predicate both surfaces render the refusal from.

import type { KeyRow } from './keys';
import { useKeys } from './keys';
import { useMyKeys } from './my-keys';
import { SESSION_TOKEN } from './session';

/**
 * Pure verdict: personal keys exist and no org key does. `undefined` on either
 * side means that list has not LANDED (in flight, or refused) and the answer is
 * `false` — a refusal line must never render off a read that did not happen,
 * the `loadedRepos` rule.
 */
export function personalKeysOnly(
  orgKeys: readonly KeyRow[] | undefined,
  myKeys: readonly KeyRow[] | undefined,
): boolean {
  if (orgKeys === undefined || myKeys === undefined) return false;
  return orgKeys.length === 0 && myKeys.length > 0;
}

/**
 * Reactive form of {@link personalKeysOnly} over the two pools' queries. Both
 * hooks are idle without a session, so this reads `false` signed out.
 */
export function useOrgKeyGate(token: string | null = SESSION_TOKEN): () => boolean {
  const org = useKeys(token);
  const mine = useMyKeys(token);
  return () => personalKeysOnly(org.data, mine.data);
}
